import { getAudioFromTangerine, getImageFromTangerine, getVideoFromTangerine } from '@oneclick/react-mint-editor';
import { defaultConfig } from 'app/config/environment';
import axios from 'axios';
import { uploadFromGoogleDrive } from 'core/crud/contents.crud';
import { postNewContent } from 'core/crud/lesson.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import { appendUploadFile } from 'core/utils/officeConversion';
import { getAcceptedExtensions, resolveContentTypeGuid } from 'core/utils/uploadFile';
import { successResponse } from 'core/utils/utils';
import { TYPESCONTENT } from 'core/variables/typesFormat';
import { useMemo, useState } from 'react';
import { useToasts } from 'react-toast-notifications';

const CONTENT_TYPE_GUIDS = { audio: 'CTTY_04', image: 'CTTY_07', video: 'CTTY_03' };

// Extensiones que `allowedUpload` fuerza a un CTTY distinto (zip→CTTY_13, pdf→CTTY_05, doc/docx→CTTY_12).
// No deben ofrecerse en un hueco de medio concreto: p. ej. elegir un .zip en un hueco de video
// crearia un contenido con el type_guid equivocado (CTTY_13 en lugar de CTTY_03).
const CROSS_TYPE_EXTENSIONS = new Set(['zip', 'pdf', 'doc', 'docx']);

function getAcceptedExtensionsForContentType(typeContent) {
  const guid = CONTENT_TYPE_GUIDS[typeContent];
  if (!guid) return getAcceptedExtensions(TYPESCONTENT);
  const extensions = getAcceptedExtensions(TYPESCONTENT.filter((type) => type.guid === guid));
  return extensions.filter((ext) => !CROSS_TYPE_EXTENSIONS.has(ext));
}

const useDialogAddContent = ({ course, onChangeMedia, onClose, type_content }) => {
  const { t } = useTranslate();
  const { addToast } = useToasts();
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [nameContent, setNameContent] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [driveInfo, setDriveInfo] = useState(null);

  const acceptedExtensions = useMemo(() => getAcceptedExtensionsForContentType(type_content), [type_content]);
  const isUploadingContent = loading || (percentage > 0 && percentage < 100);
  const isSuccessDisabled = !nameContent || (attachedFiles.length === 0 && !driveInfo?.doc);

  function resolveTypeGuid(file) {
    if (driveInfo?.doc) return driveInfo.doc.type;
    // El hueco (type_content) determina el tipo de contenido, asi que lo fijamos directamente
    // en lugar de re-resolverlo desde el MIME del fichero (que podria caer en un CTTY equivocado).
    return CONTENT_TYPE_GUIDS[type_content] ?? resolveContentTypeGuid(file, TYPESCONTENT);
  }

  function onCreate(files) {
    const file = files?.[0];
    const typeGuid = resolveTypeGuid(file);

    // Solo bloqueamos (avisando por toast y sin cerrar) cuando hay un fichero cuyo tipo no se resuelve.
    // Asi el usuario ve el motivo en lugar de que el dialogo se cierre en silencio sin subir nada.
    if (file && !driveInfo?.doc && !typeGuid) {
      addToast(t('lesson:Invalid file type'), { appearance: 'error', autoDismiss: true, duration: 3000 });
      return;
    }

    onClose();
    uploadFile(file, typeGuid);
  }

  function onChangeFiles(files) {
    setAttachedFiles(files);
  }

  function notifyCreateError() {
    addToast(t('common:An error has occurred'), { appearance: 'error', autoDismiss: true, duration: 3000 });
  }

  function onFinishCreate(guid, newContent) {
    let content = { content_guid: guid };

    if (newContent.type_guid === 'CTTY_07') {
      content = { ...getImageFromTangerine(newContent), ...content };
    } else if (newContent.type_guid === 'CTTY_04') {
      content = { ...getAudioFromTangerine(newContent), ...content };
    } else if (newContent.type_guid === 'CTTY_03') {
      content = { ...getVideoFromTangerine(newContent), ...content };
    }

    onChangeMedia(content);
    setNameContent('');
    setDriveInfo(null);
    setLoading(false);
  }

  async function uploadFile(file, typeGuid) {
    if (file || driveInfo?.doc) {
      let uploadData;

      const response = await postNewContent({
        name: nameContent,
        is_public: 1,
        type_guid: typeGuid,
        description: '',
        status: 'active',
        is_teacher_only: 0,
        mobile_friendly: 1,
        is_evaluable: 0,
        disciplines: [course.education_discipline_guid],
        educationLevels: [course.education_level_guid],
        educationYears: [course.education_year_guid],
      });

      if (!successResponse(response)) {
        notifyCreateError();
        return;
      }

      if (driveInfo?.doc) {
        setLoading(true);
        const fileDrive = await uploadFromGoogleDrive(response.data.data.guid, driveInfo.doc.id, driveInfo.token);
        if (fileDrive?.error) {
          addToast(t('lesson:Cannot upload file'), {
            appearance: 'error',
            autoDismiss: true,
            duration: 3000,
          });
          setLoading(false);
        } else if (fileDrive.data?.data) {
          onFinishCreate(response.data.data.guid, { ...response.data.data, ...fileDrive.data.data.bundle });
        } else {
          setLoading(false);
        }
      } else {
        uploadData = await axios.get(`cms/contents/${response.data.data.guid}/content/upload`, {
          headers: {
            'app-referer': defaultConfig.API_URL,
          },
        });

        if (!successResponse(uploadData)) {
          notifyCreateError();
          return;
        }

        const toUpload = uploadData.data.data.data.upload;
        startUpload(toUpload, file, response.data.data.guid, response.data.data);
      }
    }
  }

  function onChangeNameContent(e) {
    setNameContent(e.target.value);
  }

  async function startUpload(ticket, file, guid, content) {
    let { endpoint, token } = ticket;
    if (endpoint) {
      setLoading(true);
      let reader = new FileReader();

      if (endpoint && endpoint.includes('/files-storage/')) endpoint = endpoint.substr(1); //TO REMOVE THE SLASH OF THE URL

      reader.readAsDataURL(file);
      reader.onload = function () {
        sendRequest(file, reader.result, endpoint, token)
          .then((data) => {
            onFinishCreate(guid, { ...content, ...JSON.parse(data.response).data.bundle });
          })
          .catch((reason) => {
            console.log('catch error upload', reason);
          });
      };
      reader.onerror = function (error) {
        console.log('error upload', error);
      };
    }
  }

  function progress(percentageParam) {
    setPercentage(parseInt(percentageParam));
  }

  function sendRequest(file, blob, url, token) {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest();

      req.upload.addEventListener('progress', (event) => {
        const percentageParam = (event.loaded / event.total) * 100;
        progress(percentageParam);
      });

      req.upload.addEventListener('error', () => {
        reject(req.response);
      });

      req.upload.addEventListener('abort', () => {
        reject(req.response);
      });

      req.onreadystatechange = () => {
        if (req.status) {
          if (req.status === 200) {
            resolve(req.response);
          } else {
            reject(req.response);
          }
        }
      };

      const formData = new FormData();
      const filename = file.name;

      appendUploadFile(formData, file, filename);
      formData.append('token', token);

      let base = defaultConfig.BASE_URL_;
      let endpoint = `${base}${url}`;

      if (!base.endsWith('/') && !endpoint.startsWith('/')) {
        endpoint = `${base}/${url}`;
      }
      req.open('POST', endpoint);

      req.onreadystatechange = function () {
        if (req.readyState === 4) {
          if (req.status === 200) {
            resolve(req);
          } else {
            reject(req);
          }
        }
      };

      req.send(formData);
    });
  }

  function onAuthFailed(e) {
    console.log('ha habido un error, onAuthFailed', e);
  }

  function onChangeGooglePicker(data, oauthToken) {
    if (data.action === 'picked') {
      setDriveInfo({ status: data.action, doc: { ...data.docs[0], type: type_content === 'image' ? 'CTTY_07' : 'CTTY_03' }, token: oauthToken });
    } else {
      setDriveInfo({ status: data.action });
    }
  }

  function onRemoveDrive() {
    setDriveInfo(null);
  }

  return {
    t,
    acceptedExtensions,
    isSuccessDisabled,
    isUploadingContent,
    onCreate,
    attachedFiles,
    onChangeFiles,
    onChangeNameContent,
    percentage,
    onAuthFailed,
    onChangeGooglePicker,
    driveInfo,
    onRemoveDrive,
    loading,
  };
};

export default useDialogAddContent;
